<script setup>
import useToast from '@/Composables/app/useToast';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
  faClock,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onMounted, watch } from 'vue';
const { progress } = useToast();
const props = defineProps({
  toast: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['remove']);

const iconMap = {
  success: faCircleCheck,
  info: faCircleInfo,
  danger: faTriangleExclamation,
  warning: faCircleExclamation,
  loader: progress?.value !== 100 ? faClock : faCircleCheck,
};

const colorMap = {
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  loader: progress?.value !== 100 ? 'bg-info' : 'bg-success',
};

const iconColorMap = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  loader: progress?.value !== 100 ? 'text-info' : 'text-success',
};

onMounted(() => {
  if (props.toast.type !== 'loader' && !props.toast.downloadUrl) {
    setTimeout(() => emit('remove'), 5000);
  }
});
</script>

<template>
  <div
    :class="[
      'rounded-lg pl-[5px] pr-0 shadow-notification dark:shadow-toast-dark md:w-[420px]',
      colorMap[props.toast.type],
    ]"
  >
    <div
      class="flex h-full w-full items-center justify-between rounded-r-[7px] bg-white py-[25px] pr-5 dark:bg-gray-800"
    >
      <div class="flex w-full items-center gap-[25px] pl-[10px]">
        <div class="flex h-[50px] w-[50px] items-center justify-center">
          <FontAwesomeIcon
            :class="['h-9 w-9', iconColorMap[props.toast.type]]"
            :icon="iconMap[props.toast.type]"
          />
        </div>
        <div class="flex grow justify-between">
          <div class="flex flex-col">
            <h5 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ props.toast.title }}
            </h5>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ props.toast.message }}
            </p>
            <div class="mt-4" v-if="props.toast.progressable">
              <div class="mb-6 flex items-center">
                <div
                  class="hidden h-[8px] w-full overflow-hidden rounded-full bg-gray-200 text-xs dark:bg-gray-700 lg:flex"
                >
                  <div
                    :style="{ width: `${progress}%` }"
                    class="flex flex-col whitespace-nowrap bg-primary text-center text-white shadow-none"
                    :class="[progress === 100 && 'bg-success']"
                  ></div>
                </div>
                <p
                  class="w-1/12 pl-2 font-semibold"
                  :class="[progress === 100 ? 'text-success' : 'text-primary']"
                >
                  {{ progress }}%
                </p>
              </div>
              <a
                class="mt-4 rounded-md border border-gray-200 p-2 text-gray-800 dark:border-gray-600 dark:text-gray-200"
                :href="props.toast.downloadUrl"
                v-if="progress === 100 && props.toast.downloadUrl"
                >Download file</a
              >
            </div>
          </div>
          <div
            class="flex h-[14px] w-[14px] cursor-pointer items-start justify-center"
            @click="emit('remove')"
          >
            <font-awesome-icon
              class="h-5 w-5 text-gray-500 dark:text-gray-400 md:h-3 md:w-3"
              :icon="faXmark"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
